import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { GoogleSearchEvidence } from '@/components/HealthEvidence';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gsc`;

export default function DomainSearchConsole({ domain, evidence, isAdmin, reload }) {
  const [busy, setBusy] = useState(false);
  const [properties, setProperties] = useState(null);
  const [selected, setSelected] = useState('');
  const oauthEvidence = evidence?.oauth;
  const [status, setStatus] = useState(oauthEvidence || null);

  useEffect(() => {
    setProperties(null);
    setSelected('');
    setStatus(oauthEvidence || null);
  }, [domain, oauthEvidence]);

  const mapping = evidence?.mapping;
  const oauth = oauthEvidence || status;

  const action = async fn => {
    setBusy(true);
    try {
      await fn();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Search Console action failed');
    } finally {
      setBusy(false);
    }
  };

  const chooseProperty = () => action(async () => {
    const response = await axios.get(`${API}/properties`, { params: { domain } });
    setProperties(response.data.matching_properties);
    setSelected(mapping?.site_url || response.data.recommended || '');
  });

  const connectGoogle = () => action(async () => {
    const config = await axios.get(`${API}/status`);
    setStatus(config.data);
    if (!config.data.configured) {
      throw new Error('Google OAuth is not configured on the backend');
    }
    if (config.data.connected) {
      const response = await axios.get(`${API}/properties`, { params: { domain } });
      setProperties(response.data.matching_properties);
      setSelected(mapping?.site_url || response.data.recommended || '');
      return;
    }
    const response = await axios.get(`${API}/oauth/start`, { params: { domain } });
    window.location.assign(response.data.authorization_url);
  });

  const sync = () => action(async () => {
    try {
      await axios.post(`${API}/domains/${encodeURIComponent(domain)}/sync`);
    } finally {
      await reload();
    }
  });

  const link = () => action(async () => {
    await axios.put(`${API}/domains/${encodeURIComponent(domain)}/property`, { site_url: selected });
    setProperties(null);
    await reload();
  });

  const unlink = () => action(async () => {
    await axios.delete(`${API}/domains/${encodeURIComponent(domain)}/property`);
    setProperties(null);
    await reload();
  });

  const disconnect = () => action(async () => {
    await axios.delete(`${API}/oauth/connection`);
    setProperties(null);
    setStatus({ configured: true, connected: false, reauthorization_required: false });
    await reload();
  });

  const mainAction = mapping ? chooseProperty : (oauth?.connected ? chooseProperty : connectGoogle);

  return <div className="mb-6">
    <div className="flex flex-wrap items-center gap-2 mb-3">
      {!mapping && <p className="text-sm text-zinc-400 mr-2">Connect your Google account to use Search Console data for this domain.</p>}
      {isAdmin && <>
        <Button variant="outline" size="sm" disabled={busy} onClick={mainAction}>
          {mapping ? 'Change property' : oauth?.connected ? 'Select Search Console property' : 'Connect Google Search Console'}
        </Button>
        {mapping && <Button size="sm" disabled={busy} onClick={sync}>{busy ? 'Working…' : 'Sync GSC'}</Button>}
        {mapping && <Button variant="ghost" size="sm" disabled={busy} onClick={unlink}>Unlink property</Button>}
        {oauth?.connected && <Button variant="ghost" size="sm" disabled={busy} onClick={disconnect}>Disconnect Google</Button>}
      </>}
    </div>

    {status && !status.configured && <p className="text-xs text-amber-400 mb-3">Google OAuth client is not configured on the backend.</p>}
    {status?.reauthorization_required && <p className="text-xs text-amber-400 mb-3">Google authorization expired or was revoked. Connect Google Search Console again.</p>}

    {properties && <div className="flex flex-wrap gap-2 items-center mb-4">
      {properties.length ? <><label className="text-xs text-zinc-400" htmlFor="gsc-property">Search Console property</label>
        <select id="gsc-property" value={selected} onChange={event => setSelected(event.target.value)} className="bg-[#121212] text-sm text-white border border-white/20 rounded px-2 py-2 max-w-full">
          {properties.map(property => <option key={property.site_url} value={property.site_url}>{property.site_url} · {property.permission_level}</option>)}
        </select>
        <Button size="sm" disabled={busy || !selected} onClick={link}>Link property</Button></> :
        <p className="text-xs text-zinc-400">The connected Google account has no Search Console property matching this domain.</p>}
    </div>}

    {mapping && <p className="text-xs text-zinc-500 mb-3 break-all">{mapping.site_url} · Last sync {mapping.last_sync_at ? new Date(mapping.last_sync_at).toLocaleString() : 'Not synced yet'}</p>}
    {mapping?.last_error && <p className="text-xs text-amber-400 mb-3">{mapping.last_error}. Stored evidence remains available.</p>}
    {mapping?.last_inspection_error && <p className="text-xs text-zinc-400 mb-3">URL Inspection: {mapping.last_inspection_error}. Collected results are preserved.</p>}
    {mapping && !evidence?.search_reality?.available && <p className="text-sm text-zinc-400 py-3">Use Sync GSC to retrieve Search Analytics.</p>}
    <GoogleSearchEvidence data={evidence?.search_reality} analytics={evidence?.analytics} />
  </div>;
}
